import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { RxCollection } from 'rxdb';
import { useRxCollection, useRxData } from 'rxdb-hooks';

import { useMst } from '../../mobx-models/Root';
import { MiningMethodAllCap } from '../../utilities';
import { sortAscendingByCreatedOn } from '../../utilities/sortHelper';
import { Advance, AdvanceCollection, AdvanceDocument } from '../Advance/queryBuilder';
import { BlockCollection, BlockDocument } from '../Blocks/queryBuilder';
import { LocationDocument } from '../Locations/queryBuilder';
import { MiningCutDocument, MiningCutSequencePassObj } from '../MiningCut/queryBuilder';
import useMiningCuts from '../MiningCut/useMiningCuts';
import { PanelDocument } from '../Panels/queryBuilder';
import { PassDocument } from '../Passes/queryBuilder';
import { Room, RoomDocument } from '../Rooms/queryBuilder';
import { RxdbCollectionName } from '../rxdbCollectionName';
import RxdbManager from '../RxdbManager';
import { generateBaseEntity, getUnixMillisecondTimestamp } from '../rxdbUtilityFunctions';
import { SequenceDocument } from '../Sequences/queryBuilder';
import { SurveyPointDocument } from '../SurveyPoints/queryBuilder';
import { ProductionCollection, ProductionDocument } from './queryBuilder';

export interface AugmentedProduction {
  block?: BlockDocument;
  panel?: PanelDocument;
  room?: RoomDocument;
  surveyPoint?: SurveyPointDocument;
  sequence?: SequenceDocument;
  pass?: PassDocument;
  production: ProductionDocument;
  advances: AdvanceDocument[];
  miningCut?: MiningCutDocument;
  location: LocationDocument;
}

export interface ProductionLocationInfo {
  surveyPoint: SurveyPointDocument | undefined;
  miningCutSequencePassObj: MiningCutSequencePassObj | undefined;
  room: RoomDocument | undefined;
  panel: PanelDocument | undefined;
  block: BlockDocument | undefined | null;
  sequence: SequenceDocument | undefined;
  pass: PassDocument | undefined;
  mostRecentFootage: string | number | undefined | null;
}

export const useProduction = (borerShiftId?: string | null) => {
  const { shiftPicker } = useMst();

  const productionCollection: ProductionCollection = useRxCollection(
    RxdbCollectionName.BORER_SHIFT_PRODUCTION,
  );

  const productionAllDocsQueryConstructor = (collection: RxCollection) =>
    collection.find().sort({ updatedAt: 'desc' });

  const { result: productionDocsAll } = useRxData<ProductionDocument>(
    RxdbCollectionName.BORER_SHIFT_PRODUCTION,
    productionAllDocsQueryConstructor,
  );

  const advancesCollection: AdvanceCollection = useRxCollection(
    RxdbCollectionName.BORER_SHIFT_ADVANCE,
  );
  const blockCollection: BlockCollection = useRxCollection(RxdbCollectionName.BLOCKS);

  const [advancesInitialized, setAdvancesInitialized] = useState(false);
  const [productionInitialized, setProductionInitialized] = useState(false);
  const [productionForShift, setProductionForShift] = useState<AugmentedProduction[]>([]);

  const [mostRecentProductionLocationInformation, setMostRecentProductionLocationInformation] =
    useState<ProductionLocationInfo>({
      surveyPoint: undefined,
      miningCutSequencePassObj: undefined,
      room: undefined,
      panel: undefined,
      block: undefined,
      sequence: undefined,
      pass: undefined,
      mostRecentFootage: undefined,
    });

  const { getMiningCutById } = useMiningCuts();

  const productionQueryConstructor = useCallback(
    collection =>
      collection.find({
        selector: {
          borerShiftId,
        },
      }),
    [borerShiftId],
  );
  const { result: productionDocsForShift } = useRxData<ProductionDocument>(
    RxdbCollectionName.BORER_SHIFT_PRODUCTION,
    productionQueryConstructor,
  );

  useEffect(() => {
    if (productionCollection) setProductionInitialized(true);
  }, [productionCollection]);

  useEffect(() => {
    if (advancesCollection) setAdvancesInitialized(true);
  }, [advancesCollection]);

  useEffect(() => {
    (async function () {
      if (productionDocsForShift?.length) {
        const augmentedProduction: AugmentedProduction[] = await Promise.all(
          productionDocsForShift.map(async production => {
            const advances =
              (await RxdbManager?.instance.db?.collections[RxdbCollectionName.BORER_SHIFT_ADVANCE]
                ?.find({
                  selector: {
                    borerShiftProductionId: production.id,
                  },
                })
                .exec()) || [];

            const [location, miningCut] = await Promise.all([
              production.populate('locationId'),
              production.populate('miningCutId'),
            ]);

            // populate location, sequence, pass, room, survey point, panel, block
            let sequence: SequenceDocument | undefined;
            let pass: PassDocument | undefined;
            if (miningCut) {
              [sequence, pass] = await Promise.all([
                miningCut.populate('sequence'),
                miningCut.populate('pass'),
              ]);
            }

            let room: RoomDocument | undefined;
            let surveyPoint: SurveyPointDocument | undefined;
            let panel: PanelDocument | undefined;
            let block: BlockDocument | undefined;

            if (location) {
              [room, surveyPoint, panel] = await Promise.all([
                location.populate('room'),
                location.populate('surveyPoint'),
                location.populate('panel'),
              ]);
            }
            if (panel) {
              block = await panel.populate('block');
            }

            return {
              miningCut,
              production,
              advances,
              sequence,
              location,
              room,
              surveyPoint,
              block,
              panel,
              pass,
            };
          }),
        );

        augmentedProduction.sort((a, b) => (a.miningCut?.order || 0) - (b.miningCut?.order || 0));
        setProductionForShift(augmentedProduction);
      } else {
        setProductionForShift([]);
      }
    })();
  }, [productionDocsForShift]);

  const getMostRecentProductionForShiftId = async (
    bShiftId: string,
  ): Promise<ProductionDocument | undefined> => {
    if (!productionCollection) {
      return undefined;
    }

    const production = await productionCollection
      .find({
        selector: {
          borerShiftId: bShiftId,
        },
      })
      .sort({ updatedAt: 'desc' })
      .exec();

    return production.length > 0 ? production[0] : undefined;
  };

  useEffect(() => {
    const mostRecentProduction = productionDocsAll[0];
    const populateData = async () => {
      const location = await mostRecentProduction?.populate('locationId');

      const [miningCutSequencePassObj, surveyPoint, room, panel, sequence, pass, advance] =
        await Promise.all([
          getMiningCutById(mostRecentProduction?.miningCutId),
          location?.populate('surveyPoint'),
          location?.populate('room'),
          location?.populate('panel'),
          location?.populate('sequence'),
          location?.populate('pass'),
          advancesCollection
            ?.findOne({ selector: { borerShiftProductionId: mostRecentProduction?.id } })
            .sort({ updatedAt: 'desc' })
            .exec(),
        ]);
      const block = await blockCollection
        ?.findOne({
          selector: { id: panel?.block },
        })
        .exec();

      const mostRecentFootage =
        advance?.endFootage || Number(advance?.endFootage) === 0 ? advance?.endFootage : '';

      setMostRecentProductionLocationInformation({
        surveyPoint,
        miningCutSequencePassObj,
        room,
        panel,
        block,
        sequence,
        pass,
        mostRecentFootage,
      });
    };

    populateData();
  }, [productionDocsAll, advancesCollection, blockCollection, getMiningCutById]);

  const createProduction = async (
    locationId: string,
    miningCutSequencePass: MiningCutSequencePassObj,
  ) => {
    if (!shiftPicker.currentBorerShiftId) throw new Error('Missing current borer shift id');

    const prodBaseEntity = generateBaseEntity();

    const doc = {
      ...prodBaseEntity,
      borerShiftId: shiftPicker.currentBorerShiftId,
      locationId,
      miningCutId: miningCutSequencePass.miningCut.id,
    };
    return productionCollection?.insert(doc);
  };

  const updateProduction = async (
    productionToEdit: ProductionDocument,
    locationId: string,
    miningCutId: string,
  ) => {
    if (!shiftPicker.currentBorerShiftId) throw new Error('Missing current borer shift id');

    const doc = {
      id: productionToEdit.id,
      borerShiftId: productionToEdit.borerShiftId,
      locationId,
      miningCutId,
      updatedAt: getUnixMillisecondTimestamp(),
      version: productionToEdit.version,
      isDeleted: productionToEdit.isDeleted || false,
    };
    return productionCollection?.upsert(doc);
  };

  const listProductionForShiftId = async (bShiftId: string): Promise<ProductionDocument[]> => {
    if (!productionCollection) {
      return [];
    }

    const production = await productionCollection
      .find({
        selector: {
          borerShiftId: bShiftId,
        },
      })
      .exec();

    const augmentedOrderData = await Promise.all(
      production.map(async (prod, index) => ({
        miningCut: await prod.populate('miningCutId'),
        originalIndex: index,
        id: prod.id,
      })),
    );

    const sortArray = augmentedOrderData.sort((a, b) => a.miningCut.order - b.miningCut.order);

    const sortedProduction = sortArray.map(sortInfo => production[sortInfo.originalIndex]);

    return sortedProduction;
  };

  const findExistingProduction = async (
    room?: Room,
    surveyPoint?: SurveyPointDocument,
    miningCutSequencePassObj?: MiningCutSequencePassObj,
    compareRoom = true,
  ) => {
    if (!miningCutSequencePassObj) return;
    const productionsForShift = await listProductionForShiftId(shiftPicker?.currentBorerShiftId);

    let existing: ProductionDocument;
    for (let index = 0; index < productionsForShift.length; index += 1) {
      // eslint-disable-next-line no-await-in-loop
      const loc: LocationDocument = await productionsForShift[index].populate('locationId');

      if (
        (!compareRoom || (room && loc.room === room.id)) &&
        (!surveyPoint || (surveyPoint && loc.surveyPoint === surveyPoint.id)) &&
        loc.pass === miningCutSequencePassObj.pass?.id &&
        (loc.sequence === null || loc.sequence === miningCutSequencePassObj.sequence?.id)
      ) {
        existing = productionsForShift[index];
      }
    }

    return existing || undefined;
  };

  const listAdvancesForProductionId = async (productionId: string): Promise<Advance[]> => {
    if (!advancesCollection) {
      return [];
    }

    const advances = await advancesCollection
      .find({
        selector: {
          borerShiftProductionId: productionId,
        },
      })
      .exec();

    return advances.sort(sortAscendingByCreatedOn).map(x => {
      return {
        id: x.id,
        borerShiftProductionId: x.borerShiftProductionId,
        createdOn: x.createdOn,
        distance: x.distance,
        updatedAt: x.updatedAt,
        isDeleted: x.isDeleted || false,
        version: x.version,
        startDistance: x.startDistance,
        endDistance: x.endDistance,
      };
    });
  };

  const getActualTonnesAndTotalAdvance = async () => {
    // foreach Production get the sequence, then the associated miningCut tons/ft.
    // then use that with the advances and sum up
    let totalTonnes = 0;
    let totalAdvance = 0;
    const productionsForShift = await listProductionForShiftId(shiftPicker?.currentBorerShiftId);

    for (let k = 0; k < productionsForShift.length; k += 1) {
      // eslint-disable-next-line no-await-in-loop
      const cut = await productionsForShift[k].populate('miningCutId');
      // eslint-disable-next-line no-await-in-loop
      const advances = await listAdvancesForProductionId(productionsForShift[k].id);

      // eslint-disable-next-line @typescript-eslint/no-loop-func
      advances.forEach((advance: Advance) => {
        totalAdvance += parseFloat(advance.distance);
        totalTonnes += cut && cut?.density ? parseFloat(advance.distance) * cut?.density : 0;
      });
    }

    return {
      actualTotalTonnes: totalTonnes,
      actualTotalAdvance: totalAdvance,
    };
  };

  const getEndLocationForShift = async (bShiftId: string) => {
    let production: ProductionDocument;
    let advances: Advance[];
    let mostRecentDate: Dayjs;

    const productionsForShift = await listProductionForShiftId(bShiftId);
    await Promise.all(
      productionsForShift.map(async prod =>
        // eslint-disable-next-line no-return-await
        listAdvancesForProductionId(prod.id).then(advancesFor => {
          advancesFor.forEach(advance => {
            if (!mostRecentDate || mostRecentDate.isBefore(dayjs(advance.createdOn))) {
              mostRecentDate = dayjs(advance.createdOn);
              production = prod;
              advances = advancesFor;
            }
          });
        }),
      ),
    );

    if (production && advances && mostRecentDate) {
      let calculatedFootage = 0;
      advances.forEach(advance => {
        calculatedFootage += advance.distance ? parseFloat(advance.distance) : 0;
      });
      const cut = await production.populate('miningCutId');
      if (cut) {
        const sequence = await cut.populate('sequence');
        const pass = await cut.populate('pass');

        const location = await production.populate('locationId');
        if (location) {
          const room = await location.populate('room');
          const surveyPoint = await location.populate('surveyPoint');
          const panel = await location.populate('panel');
          if (panel) {
            const block = await panel.populate('block');

            return {
              location,
              block,
              panel,
              room,
              sequence,
              pass,
              surveyPoint,
              miningMethod: panel.miningMethod.toLowerCase().includes('chevron')
                ? MiningMethodAllCap.CHEVRON
                : MiningMethodAllCap.LONG_ROOM,
              footage: calculatedFootage,
            };
          }
        }
      }
    }
  };

  return {
    listAdvancesForProductionId,
    getActualTonnesAndTotalAdvance,
    getEndLocationForShift,
    productionInitialized,
    advancesInitialized,
    findExistingProduction,
    listProductionForShiftId,
    createProduction,
    updateProduction,
    productionCollection,
    advancesCollection,
    getMostRecentProductionForShiftId,
    productionForShift,
    mostRecentProductionLocationInformation,
  };
};

export default useProduction;
